import { parseAsArrayOf, parseAsInteger, parseAsString } from "nuqs";
import { useCallback } from "react";

import { useQueryStatesWithSessionStorage } from "~/shared/hooks/useQueryStatesWithSessionStorage";

export const FILTER_VISIT_RISK_STATUS_ALL = [
    "notYetPlannable", // ??
    "plannableSoon",
    "plannableOk",
    "plannableAtRisk",
    "outOfProtocol",
    "impossible",
    "planned",
];

const FILTER_VISIT_RISK_STATUS_DEFAULT = [
    "plannableOk", // ??
    "plannableAtRisk",
    "outOfProtocol",
    "impossible",
];

const plannerAreaFiltersParsers = {
    plannerStatusBucket: parseAsArrayOf(parseAsString).withDefault(FILTER_VISIT_RISK_STATUS_DEFAULT),
    clientIds: parseAsArrayOf(parseAsString).withDefault([]),
    projectIds: parseAsArrayOf(parseAsString).withDefault([]),
    plannerAreaIds: parseAsArrayOf(parseAsString).withDefault([]),
    plannedFieldworkerIds: parseAsArrayOf(parseAsString).withDefault([]),
    researchTypeIds: parseAsArrayOf(parseAsString).withDefault([]),
    maxRequiredFieldworkers: parseAsInteger,
};

export function usePlannerV2AreaFilters() {
    const [filterValues, setFilterValues] = useQueryStatesWithSessionStorage(plannerAreaFiltersParsers, {
        clearOnDefault: true,
    });

    const toggleVisitRiskStatus = useCallback(
        (status: string, checked: boolean) => {
            setFilterValues(prev => {
                const next = checked
                    ? [...new Set([...prev.plannerStatusBucket, status])]
                    : prev.plannerStatusBucket.filter(s => s !== status);
                return { plannerStatusBucket: next };
            });
        },
        [setFilterValues],
    );

    const setClientIds = useCallback(
        (ids: string[]) =>
            setFilterValues(prev => {
                // Dependents stay valid only when clients are purely added to a non-empty selection;
                // any removal (or the first client) can exclude projects/areas already selected.
                const isAdditive = prev.clientIds.length > 0 && prev.clientIds.every(clientId => ids.includes(clientId));

                return {
                    clientIds: ids,
                    ...(isAdditive ? {} : { projectIds: [], plannerAreaIds: [], researchTypeIds: [] }),
                };
            }),
        [setFilterValues],
    );

    const setProjectIds = useCallback(
        (ids: string[]) =>
            setFilterValues(ids.length > 0 ? { projectIds: ids } : { projectIds: [], plannerAreaIds: [], researchTypeIds: [] }),
        [setFilterValues],
    );

    const setFieldworkerIds = useCallback(
        (ids: string[]) =>
            setFilterValues({
                plannedFieldworkerIds: ids,
                ...(ids.length > 0 ? { plannerStatusBucket: ["planned"] } : {}),
            }),
        [setFilterValues],
    );

    const hasAnyFilters =
        [
            filterValues.clientIds,
            filterValues.projectIds,
            filterValues.plannerAreaIds,
            filterValues.plannedFieldworkerIds,
            filterValues.researchTypeIds,
        ].some(ids => ids.length > 0) || filterValues.maxRequiredFieldworkers !== null;

    return {
        filterValues,
        setFilterValues,
        toggleVisitRiskStatus,
        setClientIds,
        setProjectIds,
        setFieldworkerIds,
        hasAnyFilters,
    };
}
